/**
 * src/pages/Uso.jsx
 * Uso y churn de clientes (punto 10 / mejora #7):
 *  - A1: evolución del engagement (empresas activas + módulos abiertos por periodo)
 *  - A2: adopción por módulo (qué features se usan / se ignoran)
 *  - Tabla de churn (clic en un cliente → ficha B4/C6: su uso y sus usuarios)
 */
import { Fragment, useMemo, useState } from 'react'
import {
  ComposedChart, BarChart, Bar, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer,
} from 'recharts'
import { Header } from '../components/layout/Header'
import { Card, KpiCard } from '../components/ui/Card'
import { Badge, TIER_COLOR } from '../components/ui/Badge'
import { Table } from '../components/ui/Table'
import {
  useUso, useUsoAgregado, useUsoCliente, useUsoHorarios,
} from '../hooks/useAdminPanel'

const RANGOS = [
  { dias: 7, label: 'Semana' },
  { dias: 30, label: 'Mes' },
  { dias: 365, label: 'Año' },
]

const ESTADO = {
  activo:    { color: 'green',  label: 'activo' },
  en_riesgo: { color: 'yellow', label: 'en riesgo' },
  frio:      { color: 'red',    label: 'frío' },
  nunca:     { color: 'gray',   label: 'nunca entró' },
}

// Nombres legibles de los módulos (ids de la función S() del dashboard)
const MODULO = {
  home: 'Inicio', cat: 'Catálogo', comp: 'Competencia', sim: 'Simulador',
  oc: 'Órdenes', pred: 'Predictor', seg: 'Sin presencia', hist: 'Histórico', cfg: 'Config',
}

const ENG_COLOR = { alto: 'green', medio: 'yellow', bajo: 'gray' }
const DIAS_SEM = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom']

const CHIP_STYLE = {
  background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
  borderRadius: '5px', padding: '1px 7px', fontSize: '11px',
  color: 'var(--color-text-2)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
}

const EJE_TICK = { fill: 'var(--color-text-3)', fontSize: 11, fontFamily: 'var(--font-mono)' }
const TOOLTIP_STYLE = {
  background: 'var(--color-surface)', border: '1px solid var(--color-border)',
  borderRadius: '6px', fontSize: '12px', fontFamily: 'var(--font-mono)', color: 'var(--color-text)',
}

function fFecha(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

function etiquetaPeriodo(periodo, grano) {
  if (grano === 'mes') {
    const [y, m] = periodo.split('-').map(Number)
    return new Date(y, m - 1, 1).toLocaleDateString('es-CL', { month: 'short' })
  }
  const [, m, d] = periodo.split('-').map(Number)
  return `${d}/${m}`
}

// ── A1 + A2 (uso agregado) ────────────────────────────────────────────────────

function GraficosAgregados({ dias }) {
  const { data, isLoading } = useUsoAgregado(dias)

  const serie = useMemo(() => (data?.serie || []).map(p => ({
    ...p, label: etiquetaPeriodo(p.periodo, data?.grano),
  })), [data])

  const modulos = useMemo(() => (data?.modulos || []).map(m => ({
    ...m, label: MODULO[m.modulo] || m.modulo,
  })), [data])

  const totalClientes = data?.clientes_activos_total || 0
  const sinDatos = !isLoading && serie.every(p => !p.empresas_activas && !p.modulos_abiertos)

  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: '14px', marginBottom: '24px' }}>
      <Card title="Evolución del uso"
            subtitle={data?.grano === 'mes' ? 'Empresas activas y módulos abiertos por mes' : 'Empresas activas y módulos abiertos por día'}>
        {sinDatos ? (
          <VacioMsg>Sin uso registrado en el rango</VacioMsg>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <ComposedChart data={serie} margin={{ top: 4, right: 4, left: -22, bottom: 0 }}>
              <XAxis dataKey="label" tick={EJE_TICK} axisLine={false} tickLine={false} />
              <YAxis yAxisId="emp" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <YAxis yAxisId="mod" orientation="right" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(16,185,129,0.05)' }}
                       labelFormatter={(_, p) => p?.[0]?.payload?.periodo || ''} />
              <Legend wrapperStyle={{ fontSize: '11px', fontFamily: 'var(--font-mono)' }} />
              <Bar yAxisId="emp" dataKey="empresas_activas" name="Empresas activas"
                   fill="var(--color-accent)" radius={[3, 3, 0, 0]} maxBarSize={38} />
              <Line yAxisId="mod" type="monotone" dataKey="modulos_abiertos" name="Módulos abiertos"
                    stroke="var(--color-running)" strokeWidth={2} dot={false} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Card>

      <Card title="Adopción por módulo"
            subtitle={`Aperturas y nº de clientes que lo usan (de ${totalClientes} activos)`}>
        {(!isLoading && modulos.length === 0) ? (
          <VacioMsg>Sin módulos abiertos</VacioMsg>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={modulos} layout="vertical" margin={{ top: 0, right: 12, left: 10, bottom: 0 }}>
              <XAxis type="number" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="label" width={78} tick={EJE_TICK} axisLine={false} tickLine={false} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(16,185,129,0.05)' }}
                       formatter={(v, n, p) => [`${v} aperturas · ${p?.payload?.clientes} clientes`, p?.payload?.label]} />
              <Bar dataKey="aperturas" name="Aperturas" fill="var(--color-accent)" radius={[0, 3, 3, 0]} maxBarSize={22} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  )
}

function VacioMsg({ children }) {
  return (
    <div style={{ textAlign: 'center', padding: '48px 32px', color: 'var(--color-text-3)',
                  fontSize: '12px', fontFamily: 'var(--font-mono)' }}>
      {children}
    </div>
  )
}

// ── A3: mapa de calor hora × día ──────────────────────────────────────────────

function Heatmap({ dias }) {
  const { data, isLoading } = useUsoHorarios(dias)
  const grid = data?.grid || []
  const max = useMemo(() => Math.max(1, ...grid.flat()), [grid])

  return (
    <Card title="¿Cuándo se conectan?"
          subtitle="Logins al dashboard por día y hora (hora de Chile)"
          style={{ marginBottom: '24px' }}>
      {(!isLoading && (data?.total ?? 0) === 0) ? (
        <VacioMsg>Sin logins en el rango</VacioMsg>
      ) : (
        <div style={{ overflowX: 'auto', padding: '4px 2px' }}>
          <div style={{
            display: 'grid', gridTemplateColumns: '32px repeat(24, 1fr)',
            gap: '3px', minWidth: '660px', alignItems: 'center',
          }}>
            <div />
            {Array.from({ length: 24 }, (_, h) => (
              <div key={h} style={{
                fontSize: '9px', color: 'var(--color-text-3)',
                fontFamily: 'var(--font-mono)', textAlign: 'center',
              }}>{h % 3 === 0 ? h : ''}</div>
            ))}
            {DIAS_SEM.map((d, wd) => (
              <Fragment key={d}>
                <div style={{
                  fontSize: '10px', color: 'var(--color-text-3)',
                  fontFamily: 'var(--font-mono)', textAlign: 'right', paddingRight: '4px',
                }}>{d}</div>
                {Array.from({ length: 24 }, (_, h) => {
                  const v = grid[wd]?.[h] || 0
                  return (
                    <div key={h}
                      title={`${d} ${String(h).padStart(2, '0')}:00 · ${v} login${v === 1 ? '' : 's'}`}
                      style={{
                        aspectRatio: '1', borderRadius: '2px',
                        background: v === 0 ? 'var(--color-surface-2)'
                          : `rgba(16,185,129,${0.15 + 0.85 * (v / max)})`,
                      }} />
                  )
                })}
              </Fragment>
            ))}
          </div>
        </div>
      )}
    </Card>
  )
}

// ── B4 + C6 (ficha de uso por cliente) ────────────────────────────────────────

const USUARIO_COLUMNS = [
  {
    key: 'email', label: 'Usuario',
    render: (v, row) => (
      <span style={{ color: row.logins ? 'var(--color-text)' : 'var(--color-text-3)' }}>
        {v}{row.propietario && (
          <span style={{ marginLeft: '6px' }}><Badge color="blue">propietario</Badge></span>
        )}
        {!row.activo && <span style={{ color: 'var(--color-text-3)', fontSize: '11px' }}> · inactivo</span>}
      </span>
    ),
  },
  { key: 'logins', label: 'Logins (rango)', width: '110px', align: 'right', mono: true },
  { key: 'ultimo', label: 'Último login', width: '120px', mono: true, render: fFecha },
  {
    key: 'modulos', label: 'Módulos que usa',
    render: (v) => (v?.length
      ? <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {v.map(m => <span key={m.modulo} style={CHIP_STYLE}>{MODULO[m.modulo] || m.modulo} · {m.n}</span>)}
        </div>
      : <span style={{ color: 'var(--color-text-3)', fontSize: '11px' }}>—</span>),
  },
]

function FichaCliente({ cliente, dias, onClose }) {
  const { data, isLoading } = useUsoCliente(cliente.cliente_id, dias)

  const serie = useMemo(() => (data?.serie || []).map(p => ({
    ...p, label: etiquetaPeriodo(p.periodo, data?.grano),
  })), [data])

  const modulos = useMemo(() => (data?.modulos || []).map(m => ({
    ...m, label: MODULO[m.modulo] || m.modulo,
  })), [data])

  const sinLogins = !isLoading && serie.every(p => !p.logins)

  return (
    <Card
      title={cliente.nombre}
      subtitle={data
        ? `Último login ${fFecha(data.ultimo_acceso)} · ${data.dias_sin_acceso == null ? 'nunca entró' : `${data.dias_sin_acceso} días sin entrar`} · alta ${fFecha(data.primer_acceso)}`
        : 'Cargando…'}
      style={{ marginTop: '14px' }}
      action={
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <Badge color={TIER_COLOR[cliente.tier] || 'gray'}>{cliente.tier}</Badge>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: 'var(--color-text-3)', fontSize: '16px', lineHeight: 1,
          }}>×</button>
        </div>
      }
    >
      <div style={{ padding: '16px 20px', display: 'grid', gridTemplateColumns: '1.3fr 1fr', gap: '20px' }}>
        <div>
          <SubTitulo>LOGINS EN EL TIEMPO</SubTitulo>
          {sinLogins ? (
            <VacioMsg>Sin logins en el rango</VacioMsg>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={serie} margin={{ top: 4, right: 0, left: -26, bottom: 0 }}>
                <XAxis dataKey="label" tick={EJE_TICK} axisLine={false} tickLine={false} />
                <YAxis allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(16,185,129,0.05)' }}
                         labelFormatter={(_, p) => p?.[0]?.payload?.periodo || ''} />
                <Bar dataKey="logins" name="Logins" fill="var(--color-running)" radius={[3, 3, 0, 0]} maxBarSize={30} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
        <div>
          <SubTitulo>MÓDULOS QUE USA</SubTitulo>
          {(!isLoading && modulos.length === 0) ? (
            <VacioMsg>No ha abierto ningún módulo</VacioMsg>
          ) : (
            <ResponsiveContainer width="100%" height={150}>
              <BarChart data={modulos} layout="vertical" margin={{ top: 0, right: 10, left: 8, bottom: 0 }}>
                <XAxis type="number" allowDecimals={false} tick={EJE_TICK} axisLine={false} tickLine={false} />
                <YAxis type="category" dataKey="label" width={74} tick={EJE_TICK} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: 'rgba(16,185,129,0.05)' }} />
                <Bar dataKey="n" name="Aperturas" fill="var(--color-accent)" radius={[0, 3, 3, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>

      <div style={{ borderTop: '1px solid var(--color-border)' }}>
        <div style={{ padding: '12px 20px 4px', display: 'flex', alignItems: 'baseline', gap: '10px' }}>
          <SubTitulo>USUARIOS DE LA EMPRESA (ASIENTOS)</SubTitulo>
          {data?.modulos_sin_atribuir > 0 && (
            <span style={{ fontSize: '10px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>
              +{data.modulos_sin_atribuir} aperturas previas a la medición por usuario (sin atribuir)
            </span>
          )}
        </div>
        <Table columns={USUARIO_COLUMNS} data={data?.usuarios} loading={isLoading}
               emptyMessage="Sin usuarios registrados" />
      </div>
    </Card>
  )
}

function SubTitulo({ children }) {
  return (
    <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)',
                  letterSpacing: '0.08em', marginBottom: '8px' }}>
      {children}
    </div>
  )
}

// ── Tabla de churn ────────────────────────────────────────────────────────────

const COLUMNS = [
  {
    key: 'nombre', label: 'Cliente',
    render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500, color: 'var(--color-text)' }}>{v}</div>
        <div style={{ fontSize: '11px', color: 'var(--color-text-3)', fontFamily: 'var(--font-mono)' }}>{row.cliente_id}</div>
      </div>
    ),
  },
  { key: 'tier', label: 'Tier', width: '80px', render: v => <Badge color={TIER_COLOR[v] || 'gray'}>{v}</Badge> },
  {
    key: 'estado', label: 'Estado', width: '110px',
    render: v => { const e = ESTADO[v] || ESTADO.nunca; return <Badge color={e.color} dot>{e.label}</Badge> },
  },
  {
    key: 'engagement', label: 'Engagement', width: '110px', align: 'right',
    render: (v, row) => (
      <Badge color={ENG_COLOR[row.engagement_banda] || 'gray'}>{v ?? 0}</Badge>
    ),
  },
  {
    key: 'dias_sin_acceso', label: 'Sin entrar', width: '90px', align: 'right', mono: true,
    render: v => v == null ? '—' : `${v} d`,
  },
  { key: 'ultimo_acceso', label: 'Último login', width: '110px', mono: true, render: fFecha },
  { key: 'accesos_7d', label: 'Logins 7d', width: '80px', align: 'right', mono: true },
  { key: 'accesos_win', label: 'Logins rango', width: '95px', align: 'right', mono: true },
  { key: 'n_modulos', label: 'Módulos', width: '80px', align: 'right', mono: true },
]

export default function Uso() {
  const [dias, setDias] = useState(30)
  const [sel, setSel] = useState(null)   // cliente seleccionado para la ficha
  const { data, isLoading } = useUso(dias)
  const r = data?.resumen || {}

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Header
        title="Uso y churn"
        subtitle="Engagement de los clientes: evolución, adopción de módulos y riesgo de abandono"
        action={
          <div style={{ display: 'flex', gap: '4px' }}>
            {RANGOS.map(x => (
              <button
                key={x.dias}
                onClick={() => { setDias(x.dias); setSel(null) }}
                style={{
                  border: '1px solid var(--color-border)',
                  background: dias === x.dias ? 'var(--color-accent-dim)' : 'var(--color-surface)',
                  color: dias === x.dias ? 'var(--color-accent)' : 'var(--color-text-2)',
                  borderRadius: '6px', padding: '5px 12px', cursor: 'pointer',
                  fontSize: '12px', fontFamily: 'var(--font-mono)',
                }}
              >
                {x.label}
              </button>
            ))}
          </div>
        }
      />

      <div style={{ flex: 1, overflow: 'auto', padding: '24px 28px' }}>
        <GraficosAgregados dias={dias} />

        <Heatmap dias={dias} />

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '14px', marginBottom: '24px' }}>
          <KpiCard label="ACTIVOS" value={isLoading ? '—' : (r.activos ?? 0)}
                   color="var(--color-ok)" sub="login < 7 días" />
          <KpiCard label="EN RIESGO" value={isLoading ? '—' : (r.en_riesgo ?? 0)}
                   color="#f59e0b" sub="sin entrar 7–14 días" />
          <KpiCard label="FRÍOS" value={isLoading ? '—' : (r.frios ?? 0)}
                   color={r.frios > 0 ? 'var(--color-error)' : 'var(--color-text-3)'}
                   sub="14+ días o nunca" />
        </div>

        <Card title="Clientes por riesgo de abandono"
              subtitle="Ordenados por tiempo sin entrar (los más fríos, arriba) · clic en un cliente para ver su ficha de uso">
          <Table
            columns={COLUMNS}
            data={data?.clientes}
            loading={isLoading}
            onRowClick={row => setSel(row)}
            emptyMessage="Sin clientes activos"
          />
        </Card>

        {sel && <FichaCliente cliente={sel} dias={dias} onClose={() => setSel(null)} />}
      </div>
    </div>
  )
}
